import { FC, useState, useEffect, useRef } from 'react';

import { ThemeColors } from 'styled-components';
import { differenceInMinutes, differenceInSeconds } from 'date-fns';

import { CallPriorityEnum } from '~/enums/CallPriorityEnum';

import { Container } from './styles';

import { Timer } from '../Timer';

interface ICardProps {
  readonly tableNumber: string;
  readonly calledAt: Date;
  readonly limitMinutes: number;
  readonly ranges: Record<ThemeColors, number>;
  readonly onExpire: () => Promise<void>;
}

export const Card: FC<ICardProps> = ({
  tableNumber,
  ranges,
  limitMinutes,
  calledAt,
  onExpire,
}) => {
  const calledExpirationRef = useRef<boolean>(false);
  const [seconds, setSeconds] = useState<number>(
    differenceInSeconds(new Date(), new Date(calledAt))
  );
  const [priority, setPriority] = useState<CallPriorityEnum>(
    CallPriorityEnum.neutral
  );

  useEffect(() => {
    if (!ranges) return;
    const { lowPriority, hightPriority, veryHightPriority } = ranges;
    const { current: hasCalledExpirationFn } = calledExpirationRef;
    const diffSeconds = differenceInSeconds(new Date(), new Date(calledAt));
    const diffMinutes = differenceInMinutes(new Date(), new Date(calledAt));

    if (diffMinutes >= limitMinutes && !hasCalledExpirationFn) {
      onExpire();
      calledExpirationRef.current = true;
      return;
    }

    if (diffSeconds >= lowPriority && diffSeconds < hightPriority) {
      setPriority(CallPriorityEnum.low);
      return;
    }

    if (diffSeconds >= hightPriority && diffSeconds <= veryHightPriority) {
      setPriority(CallPriorityEnum.hight);
      return;
    }

    if (diffSeconds >= veryHightPriority) {
      setPriority(CallPriorityEnum.veryHight);
    }
  }, [calledAt, ranges, seconds, limitMinutes, tableNumber, onExpire]);

  function addSecond(): void {
    setSeconds((prev) => prev + 1);
  }

  useEffect(() => {
    const timerId = setInterval(addSecond, 1000);
    return function cleanup() {
      clearInterval(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container priority={priority}>
      <span>{tableNumber}</span>
      <Timer time={seconds} />
    </Container>
  );
};
